import React, { MouseEventHandler } from 'react';
import PropTypes from 'prop-types';
import type { Validator } from 'prop-types';
import { LinkComponent, LinkProps } from './types';
import {
  StyledLinkInherited,
  StyledLinkElement,
  StyledLinkIcon,
  StyledLink,
  StyledButtonLink,
} from './Link.styles';
import { mapLinkSizeToIconSize } from './utils';
import { PolymorphicRef } from '../utils/types';

const LINK_DEFAULT_SIZE = 'default';

function _Link<C extends 'button' | 'a' = 'a'>(props: LinkProps<C>, ref: PolymorphicRef<C>) {
  const {
    children,
    color = 'default',
    size = LINK_DEFAULT_SIZE,
    id,
    icon,
    alignIcon = 'end',
    underlined = true,
    disabled,
    inherited,
    weight = 'default',
    onClick,
    href = '',
    isActive,
    ...otherProps
  } = props;

  const commonProps = {
    ref,
    weight,
    color,
    size,
    underlined,
    disabled,
    inherited,
    id,
    isActive,
    ...otherProps,
  };

  if (color === 'highlight') {
    console.warn(
      'The "highlight" value for color in `Link` is deprecated. Please use "emphasized" instead.'
    );
  }

  const iconContent = icon ? (
    <StyledLinkIcon size={size} alignIcon={alignIcon}>
      {React.cloneElement(icon, {
        // Color property in icon will be ignored, as we want to make sure that icon and text color in the link always match.
        color: 'currentColor',
        size: size ? mapLinkSizeToIconSize[size] : mapLinkSizeToIconSize['small'],
      })}
    </StyledLinkIcon>
  ) : null;

  const commonContent = (
    <>
      {icon && alignIcon === 'start' && iconContent}
      {children &&
        (inherited ? (
          <StyledLinkInherited>{children}</StyledLinkInherited>
        ) : (
          <StyledLinkElement as="span">{children}</StyledLinkElement>
        ))}
      {icon && alignIcon === 'end' && iconContent}
    </>
  );

  if (props.as === 'button') {
    return (
      <StyledButtonLink onClick={onClick as MouseEventHandler<HTMLButtonElement>} {...commonProps}>
        {commonContent}
      </StyledButtonLink>
    );
  }

  return (
    <StyledLink
      href={href}
      {...commonProps}
      onClick={
        disabled
          ? (e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
            }
          : (onClick as MouseEventHandler<HTMLAnchorElement>)
      }
      {...(disabled ? { tabIndex: -1 } : {})}
    >
      {commonContent}
    </StyledLink>
  );
}

export const Link = React.forwardRef(_Link) as LinkComponent;

const checkInherited: Validator<true> = (props) => {
  const p = props as LinkProps;
  if (p.inherited && (p.color !== undefined || p.size !== undefined)) {
    return new Error('When passing inherited property, color and size property should not be used');
  }

  return null;
};

Link.propTypes = {
  children: PropTypes.node,
  inherited: checkInherited,
  color: PropTypes.oneOf(['default', 'inverse', 'subdued', 'highlight', 'emphasized'] as const),
  size: PropTypes.oneOf(['xsmall', 'small', 'default', 'large'] as const),
  weight: PropTypes.oneOf(['default', 'strong'] as const),
  id: PropTypes.string,
  target: PropTypes.oneOf(['_blank', '_self', '_parent', '_top'] as const),
  href: PropTypes.string,
  icon: PropTypes.element,
  alignIcon: PropTypes.oneOf(['start', 'end'] as const),
  disabled: PropTypes.bool,
  underlined: PropTypes.bool,
  onClick: PropTypes.func,
  isActive: PropTypes.bool,
};
